import { useState } from 'react';
import { Pencil, Plus } from 'lucide-react';
import { useAdminProducts, useAdminSuppliers } from '@/lib/queries';
import { AdminLayout } from '@/components/AdminLayout';
import { ChannelChip } from '@/components/ChannelChip';
import { ProductFormDialog } from '@/components/ProductFormDialog';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import type { AdminProduct } from '@/lib/types';

// Products & Supplier Mapping — dense admin table managing the catalog and the
// product-to-supplier routing rules.
export function ProductSupplierMapping() {
  const [search, setSearch] = useState('');
  const { data } = useAdminProducts(search);
  const { data: suppliers } = useAdminSuppliers();
  const [editing, setEditing] = useState<AdminProduct | 'new' | null>(null);

  const products = data?.data ?? [];
  const supplierOptions = suppliers?.data ?? [];
  const categories = [...new Set(products.map((p) => p.category).filter((c): c is string => !!c))].sort();

  return (
    <AdminLayout>
      <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="page-title">Products &amp; Supplier Mapping</h1>
          <p className="page-sub">Manage the catalog and per-product routing rules.</p>
        </div>
        <Button onClick={() => setEditing('new')}>
          <Plus className="h-4 w-4" /> Add Product
        </Button>
      </div>

      <input
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Search by product name, supplier, category…"
        className="mb-6 h-10 w-full max-w-md rounded-card border border-line bg-surface-2 px-3 text-sm text-ink placeholder:text-faint focus:border-brand focus:outline-none"
      />

      <Card className="overflow-x-auto">
        <table className="w-full text-left text-sm">
          <thead>
            <tr className="border-b border-line text-muted">
              <th className="px-4 py-3 font-medium">Product</th>
              <th className="px-4 py-3 font-medium">Category</th>
              <th className="px-4 py-3 font-medium">Primary Supplier</th>
              <th className="px-4 py-3 font-medium">SKU/Code</th>
              <th className="px-4 py-3 font-medium">Channel</th>
              <th className="px-4 py-3 font-medium">Fallback Suppliers</th>
              <th className="px-4 py-3 font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {products.map((p) => (
              <tr key={p.id} className="border-b border-line hover:bg-surface-2">
                <td className="px-4 py-3 text-ink">{p.name}</td>
                <td className="px-4 py-3 text-muted">{p.category ?? '—'}</td>
                <td className="px-4 py-3 text-ink">{p.primarySupplierName ?? '—'}</td>
                <td className="px-4 py-3 font-mono text-muted">{p.primarySku}</td>
                <td className="px-4 py-3">
                  <ChannelChip channel={p.dispatchChannel} />
                </td>
                <td className="px-4 py-3">
                  {p.fallbacks.length === 0 ? (
                    <span className="text-faint">— None —</span>
                  ) : (
                    <ol className="flex flex-wrap gap-1.5">
                      {p.fallbacks.map((f, i) => (
                        <li
                          key={f.supplierId}
                          title={`${f.supplierName} · ${f.sku}`}
                          className="inline-flex items-center gap-1.5 whitespace-nowrap border border-line bg-surface-2 px-2 py-0.5 text-xs text-ink"
                        >
                          <span className="font-mono text-brand">{i + 1}</span>
                          {f.supplierName}
                        </li>
                      ))}
                    </ol>
                  )}
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <button
                      className="rounded p-1.5 text-muted hover:bg-surface-3 hover:text-brand"
                      aria-label={`Edit ${p.name}`}
                      onClick={() => setEditing(p)}
                    >
                      <Pencil className="h-4 w-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {products.length === 0 && (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-muted">
                  No products found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </Card>

      <ProductFormDialog
        open={editing !== null}
        onClose={() => setEditing(null)}
        product={editing === 'new' ? null : editing}
        suppliers={supplierOptions}
        categories={categories}
      />
    </AdminLayout>
  );
}
